import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { defaultRoute } from "@/components/RoleRoute";
import { GFS_LOGO_URL } from "@/lib/brand";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1669452122541-15349927d885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmb3Jlc3QlMjBncmVlbiUyMHRleHR1cmV8ZW58MHx8fHwxNzc3MzkyMDMyfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role) navigate(defaultRoute(user.role, user.status), { replace: true });
  }, [user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      if (u?.must_change_password) {
        navigate("/first-login", { state: { token: u.change_password_token }, replace: true });
        return;
      }
      navigate(defaultRoute(u.role, u.status), { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left: hero panel */}
      <div
        className="relative hidden lg:flex items-end p-12"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(5,104,57,0.85), rgba(0,0,0,0.45)), url(${LOGIN_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img
              src={GFS_LOGO_URL}
              alt="GreenForest Solutions"
              className="w-12 h-12 object-contain bg-white/95 rounded-lg p-1"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide">GreenForest Solutions</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">
                Traceability
              </div>
            </div>
          </div>
          <h1 className="font-heading text-5xl font-semibold leading-tight tracking-tight">
            Every kilogram. <br />
            Every step. <br />
            <span className="text-[#D2E4D6]">Traced.</span>
          </h1>
          <p className="mt-6 text-sm text-white/80 leading-relaxed">
            PROcess is the packaging traceability core that connects collectors,
            facilities and recyclers across the value chain — without the friction.
          </p>
          <div className="mt-10 flex gap-6 text-xs uppercase tracking-[0.2em] text-white/70">
            <div>Source</div>
            <div>→</div>
            <div>Collect</div>
            <div>→</div>
            <div>Process</div>
            <div>→</div>
            <div>Recover</div>
          </div>
        </div>
      </div>

      {/* Right: login form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-[#F9F8F6]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src={GFS_LOGO_URL}
              alt="GreenForest Solutions"
              className="w-12 h-12 object-contain"
            />
            <div className="leading-tight">
              <div className="font-heading text-2xl font-semibold">
                PRO<span className="text-[#056839]">cess</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#69A488]">
                GreenForest Solutions
              </div>
            </div>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000]">
            Sign in
          </h2>
          <p className="text-sm text-[#3F3F3F] mt-2">
            Welcome back. Continue tracing packaging waste.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-[#69A488]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gfs.earth"
                required
                data-testid="login-email-input"
                className="mt-2 h-12 border-[#E4D5B7] focus-visible:ring-[#056839]/20 focus-visible:border-[#056839]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-[#69A488]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-2 h-12 border-[#E4D5B7] focus-visible:ring-[#056839]/20 focus-visible:border-[#056839]"
              />
            </div>
            {err && (
              <div
                className="text-sm bg-[#A0522D]/10 text-[#A0522D] px-3 py-2 rounded-lg"
                data-testid="login-error"
              >
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-12 gfs-bg-primary hover:bg-[#044E2A] text-white rounded-lg font-medium"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#D2E4D6]">
            <p className="text-center text-sm text-[#3F3F3F]">Don&apos;t have an account? Contact your administrator.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
